import Modal from "common/Modal/Modal";
import Text from "common/Typography/Text";
import Flex from "common/Boxes/Flex";
import CurrentUsageValue from "features/settings/Quota/CurrentUsageValue";
import QuotaProgressBar from "features/settings/Quota/QuotaProgressBar";
import { QuotaModalProps } from "@shared/features/settings/types";
import { USAGE_TYPE_TO_ICON } from "features/settings/common/constants";
import PassedQuotaContent from "features/settings/Quota/PassedQuotaContent";
import { useUser } from "@shared/utils/hooks/misc";
import { getUserExpirationInfo } from "@shared/utils/misc";
import {
  useBottomNavigator,
  useRestrictedCountriesPurchase,
} from "utils/hooks/misc";
import {
  BASE_PURCHASE_SUBSCRIPTION_SETTINGS_PATH,
  IS_WEB_VIEW,
  PURCHASE_API_SLOTS_SETTINGS_PATH,
} from "common/constants";
import Button from "common/Buttons/Button";
import { showIntercomSpace } from "utils/misc.tsx";
import Link from "common/Typography/Link.tsx";
import ArrowLeftOutlined from "@ant-design/icons/ArrowLeftOutlined";
import styled, { useTheme } from "styled-components";
import { USAGE_TYPE_TO_LUCIDE_ICON } from "features/settings/NewLook/usageIcons.ts";
import { NEW_LOOK_ICON_STROKE } from "common/NewLook";
import { useHistory } from "react-router-dom";
import React from "react";
import { getClientQuotaText } from "features/settings/common/utils.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation.ts";
import { LanguageNameSpaces } from "@shared/types";
import { useHideBillingButtons } from "features/settings/common/hooks.tsx";
import { isWhitelabelSubscriptionType } from "@shared/utils/misc";

const QuotaModal = ({
  isOpen,
  onCancel,
  title,
  currentUsage,
  usageLimit,
  currentUsageObjectText,
  nextPlanData,
  usageType,
  currentUsageName,
  isSubscriptionExpired,
  okText,
  centered,
  isMaxPlan,
  isMaxPlanClientQuota,
  isPremiumPlanClientQuota,
}: QuotaModalProps) => {
  const { user } = useUser();
  const history = useHistory();
  const { onPurchaseClick } = useRestrictedCountriesPurchase();
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const { t } = useTypedTranslation(LanguageNameSpaces.BILLING);
  const { isNewLook } = useTheme();

  const getCurrentUsage = () => {
    if (!usageType) return;
    const Icon = USAGE_TYPE_TO_ICON[usageType];
    const NewLookIcon = USAGE_TYPE_TO_LUCIDE_ICON[usageType];
    return (
      <>
        <Flex style={{ marginBottom: 10 }}>
          <Text
            type="secondary"
            as="div"
            style={{ display: "flex", alignItems: "center" }}
          >
            {isNewLook && NewLookIcon ? (
              <NewLookIcon
                size={16}
                strokeWidth={NEW_LOOK_ICON_STROKE}
                style={{ marginRight: 8 }}
              />
            ) : (
              <Icon style={{ fontSize: 16, marginRight: 8 }} />
            )}
            {currentUsageName}
          </Text>
          <CurrentUsageValue
            usageType={usageType}
            currentUsage={currentUsage}
            usageLimit={usageLimit}
          />
        </Flex>
        <QuotaProgressBar percent={100} />
      </>
    );
  };

  const getTopRow = () => {
    if (isSubscriptionExpired) {
      const formattedExpiration = getUserExpirationInfo(user, t)[1];
      return (
        <Flex style={{ marginBottom: 16 }}>
          <Text style={{ fontWeight: 500 }}>
            {t("quota_modal.plan")} -{" "}
            {user?.is_trial ? `${t("quota_modal.trial")} -` : ""}
            {user?.package}
          </Text>
          <Text style={{ fontWeight: 500 }} type="danger">
            {t("quota_modal.expired_on", { expiration: formattedExpiration })}
          </Text>
        </Flex>
      );
    } else {
      return getCurrentUsage();
    }
  };

  const hideBillingButtons = useHideBillingButtons();
  const canPurchaseApiSlots = isWhitelabelSubscriptionType(
    user?.subscription_plan_type
  );

  const onOk = () => {
    if (isSubscriptionExpired) {
      if (hideBillingButtons) {
        onCancel();
      } else {
        onPurchaseClick(BASE_PURCHASE_SUBSCRIPTION_SETTINGS_PATH);
      }
    } else if (isMaxPlanClientQuota || isPremiumPlanClientQuota) {
      if (IS_WEB_VIEW) {
        showIntercomSpace("home");
      } else {
        history.push(PURCHASE_API_SLOTS_SETTINGS_PATH);
      }
    } else if (isMaxPlan) {
      onCancel();
    }
  };

  const maxClientQuotaButton = (
    <Link onClick={onOk}>
      {okText}
      {!hideBillingButtons && <Arrow />}
    </Link>
  );
  const getContent = () => {
    if (isSubscriptionExpired) {
      return <Text as="div">{t("quota_modal.renew_subscription")}</Text>;
    } else if (isMaxPlanClientQuota) {
      return (
        <Text as="div">
          {t("quota_modal.reach_max_number")}{" "}
          <Text
            style={{ fontWeight: isNewLook ? 500 : 600, display: "inline" }}
          >
            {t("quota_modal.primary_api_slots")}
          </Text>{" "}
          {getClientQuotaText(t, false, canPurchaseApiSlots)}
        </Text>
      );
    }
    return (
      <PassedQuotaContent
        currentUsageObjectText={currentUsageObjectText}
        nextPlanData={nextPlanData}
        isPremiumPlanClientQuota={isPremiumPlanClientQuota}
        canPurchaseApiSlots={canPurchaseApiSlots}
        additionalButton={
          isPremiumPlanClientQuota && canPurchaseApiSlots
            ? maxClientQuotaButton
            : undefined
        }
      />
    );
  };

  const footer = !!(isMaxPlan || isSubscriptionExpired) && (
    <Flex $justifyContent="flex-end">
      {isSubscriptionExpired && (
        <Button onClick={onCancel} style={{ marginRight: 8 }}>
          Cancel
        </Button>
      )}
      {isMaxPlanClientQuota && canPurchaseApiSlots ? (
        maxClientQuotaButton
      ) : (
        <Button onClick={onOk} type="primary">
          {okText || "OK"}
          {!hideBillingButtons && <Arrow />}
        </Button>
      )}
    </Flex>
  );

  return (
    <Modal
      open={isOpen}
      footer={footer}
      onCancel={onCancel}
      title={title}
      destroyOnHidden={true}
      centered={centered}
      styles={{
        body: { padding: isBottomNavigator ? 12 : undefined },
      }}
    >
      {getTopRow()}
      {getContent()}
    </Modal>
  );
};

export default React.memo(QuotaModal);

const Arrow = styled(ArrowLeftOutlined)`
  transform: rotate(180deg);
  margin-left: 8px;
`;
